import { AxiosResponse } from "axios";
import { apiClient } from "../constants";
import {
  MemberWallPosts,
  UserProfile,
  UserProfileDepricated,
  listUserWallPostsProps,
} from "./types";

const getPath = ({ memberID }: listUserWallPostsProps) =>
  `/v1/listUserWallPosts?userID=${memberID}`;

interface Bootcamp {
  id: string;
  name: string;
}

interface UserResponse {
  id: string;
  familyName: string;
  givenName: string;
  profileImg: string;
  role: string;
  lastActive: string; // ISO timestamp
  streakCount: number;
  bootcamp: Bootcamp;
  profileBackgroundImgixURL: string;
}

export type User = {
  id: string;
  givenName: string;
  familyName: string;
  profileImgImgixURL: string;
  profileImg: string;
  teamID: string;
  roleName: string;
};

export type UsersResponse = {
  users: User[];
};

export const getUser = async (userID: string): Promise<User | null> => {
  if (!userID) return null;

  const response: AxiosResponse<UserResponse> = await apiClient.get(
    `/users/${userID}`
  );
  const data = response.data;

  const user: User = {
    ...data,
    id: data.id,
    givenName: data.givenName,
    familyName: data.familyName,
    profileImgImgixURL: data.profileBackgroundImgixURL, // assuming this maps correctly, might be wrong
    profileImg: data.profileImg,
    teamID: data.bootcamp?.id || "", // fallback to empty string if bootcamp is missing
    roleName: data.role,
  };

  return user;
};
export const searchUsers = async (
  term: string,
  options: { signal?: AbortSignal } = {}
): Promise<UsersResponse | null> => {
  if (!term) return null;

  const response: AxiosResponse<UsersResponse> = await apiClient.get(
    `/v1/searchUsers?term=${encodeURIComponent(term)}`,
    { signal: options.signal }
  );

  return response.data;
};

export const listUserWallPosts = async ({
  memberID,
  ...props
}: listUserWallPostsProps): Promise<MemberWallPosts> => {
  if (!memberID) {
    throw new Error("Member ID is required");
  }

  const response: AxiosResponse<MemberWallPosts> = await apiClient.get(
    getPath({ memberID, ...props })
  );

  return response.data;
};

export const updateMember = async (
  memberID: string,
  data: UserProfile
): Promise<any> => {
  if (!memberID) {
    throw new Error("Member ID is required");
  }

  const response: AxiosResponse<UserProfile> = await apiClient.patch(
    `/users/${memberID}`,
    data
  );

  return response.data;
};

export const toggleUserWorkoutFavorite = async (data: {
  userID: string;
  workoutID: string;
  isFavorited: boolean;
}): Promise<{ affectedUserWorkoutFavoriteID?: string }> => {
  const response = await apiClient.post(`v1/toggleUserWorkoutFavorite`, data);

  return response.data;
};

export const getUserKEK = async () => {
  const response = await apiClient.get<{
    kekBase64: string;
  }>(`v1/getUserKEK`);

  return response.data;
};

export const getMemberByID = async (
  memberID: string
): Promise<UserProfileDepricated> => {
  const response = await apiClient.get(`/users/${memberID}`);

  return response.data;
};

/**
 * Check if a user is an admin based on their role
 * @param user User object from Rocky API with role property
 * @returns true if the user is an admin, false otherwise
 */
export function isUserAdmin(user: { role?: string }): boolean {
  return user?.role === "admin";
}
